import request from "@/lib/api/handler";

import { useAuth0 } from "@auth0/auth0-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { QueryKeys } from "@/queries/utils";
import { Conversation } from "@/queries/venturi/venturi-job-chat";
import {
  AdminJobCandidateDetails,
  AdminJobDetails,
} from "@/queries/admin/job-details";

export const useSendVenturiJobChatMessage = (chatId: string, jobId: string) => {
  const qc = useQueryClient();
  const { getAccessTokenSilently } = useAuth0();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async (message: string) => {
      const token = await getAccessTokenSilently();

      return request<{ message: string; candidate?: AdminJobCandidateDetails }>(
        {
          method: "POST",
          path: `/api/admin/job_opening/${jobId}/venturi_chat/${chatId}`,
          token,
          body: JSON.stringify({
            message,
          }),
        },
      );
    },
    onMutate: (variables) => {
      // Fetch any existing data for the job updated
      const prevMessages = qc.getQueryData<Conversation>(
        QueryKeys.venturiJobChat(chatId),
      );

      if (prevMessages) {
        if (prevMessages.initialized) {
          // Update the data
          qc.setQueryData<Conversation>(QueryKeys.venturiJobChat(chatId), {
            ...prevMessages,
            messages: [
              ...prevMessages.messages,
              {
                from: "User",
                message: variables,
              },
            ],
          });
        } else {
          // Update the data
          qc.setQueryData<Conversation>(QueryKeys.venturiJobChat(chatId), {
            ...prevMessages,
            initialized: true,
          });
        }
      }
    },
    onSuccess: (data) => {
      // Fetch any existing data for the job updated
      const prev = qc.getQueryData<Conversation>(
        QueryKeys.venturiJobChat(chatId),
      );

      if (prev) {
        // Update the data
        qc.setQueryData<Conversation>(QueryKeys.venturiJobChat(chatId), {
          ...prev,
          messages: [
            ...prev.messages,
            {
              from: "Bot",
              message: data.message,
            },
          ],
        });
      }

      // If a new candidate was also returned, add them to our list
      qc.setQueryData<AdminJobDetails>(
        QueryKeys.adminJobDetails(jobId),
        (old) => {
          const { candidate } = data;

          if (!old || !candidate) {
            return old;
          }

          return {
            ...old,
            candidates: [
              candidate,
              ...old.candidates.filter(({ id }) => id !== candidate.id),
            ],
          };
        },
      );
    },
  });

  return {
    sendVenturiJobChatMessage: mutate,
    isSendingVenturiJobChatMessage: isPending,
    sendVenturiJobChatMessageError: error,
  };
};
